import { useCallback, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';

import { Typography, useMediaQuery } from '@mui/material';
import { RiSideBarLine } from '@remixicon/react';

import {
  AccountUserDto,
  CalendarEventDto,
  CalendarMeetingDto,
  MeetingAnalysisDto
} from '@/api/generated';
import { Content } from '@/components/Meeting/MeetingStyles';
import { DrawerOpenButton } from '@/components/Meeting/components/MeetingData/MeetingDataStyles';

import MeetingData from './components/MeetingData/MeetingData';
import MeetingDrawer from './components/MeetingDrawer/MeetingDrawer';

const Meeting = ({
  meeting,
  meetingRecordingUrl,
  meetingTranscriptUrl,
  event,
  eventLoading = false,
  usersList,
  sharedMeeting = false,
  meetingAnalysis
}: {
  meeting: CalendarMeetingDto | undefined;
  meetingRecordingUrl: string | undefined;
  meetingTranscriptUrl: string | undefined;
  event: CalendarEventDto | undefined;
  eventLoading?: boolean;
  usersList?: AccountUserDto[];
  sharedMeeting?: boolean;
  meetingAnalysis?: MeetingAnalysisDto | undefined;
}) => {
  const reactPlayerRef = useRef<ReactPlayer>(null);
  const transcriptContainerRef = useRef<HTMLUListElement>(null);
  const [videoParts, setVideoParts] = useState<Record<string, string>>({});
  const [markedId, setMarkedId] = useState<string>('');
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isVideoLoading, setIsVideoLoading] = useState<boolean>(true);
  const isLargeScreen = useMediaQuery('(min-width: 1200px)');
  const [open, setOpen] = useState<boolean>(isLargeScreen);

  useEffect(() => {
    if (isLargeScreen) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isLargeScreen]);

  useEffect(() => {
    if (!transcriptContainerRef.current) return;
    const transcriptContainerNode = transcriptContainerRef.current;
    transcriptContainerNode?.addEventListener('wheel', onTranscriptUserScroll);
    transcriptContainerNode?.addEventListener('touchmove', onTranscriptUserScroll);

    return () => {
      transcriptContainerNode?.removeEventListener('wheel', onTranscriptUserScroll);
      transcriptContainerNode?.removeEventListener('touchmove', onTranscriptUserScroll);
    };
  }, [transcriptContainerRef.current]);

  const onVideoProgress = useCallback(
    (playedSeconds: number) => {
      const key = playedSeconds | 0;
      const id = videoParts ? videoParts[key] : '';

      const newMarkedLine = document.getElementById(id);
      if (!newMarkedLine) return;
      if (!isScrolling) newMarkedLine.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setMarkedId(id);
    },
    [videoParts, isScrolling]
  );

  const onTranscriptUserScroll = () => {
    setIsScrolling(true);
  };

  const onVideoReady = useCallback(() => {
    setIsVideoLoading(false);
  }, [setIsVideoLoading]);

  const onTimeClicked = (time: number | undefined) => {
    if (time !== undefined) {
      reactPlayerRef?.current?.seekTo(time, 'seconds');
    }
  };

  return (
    <Content className="meeting">
      <DrawerOpenButton onClick={() => setOpen(true)}>
        <RiSideBarLine size={16} />
        <Typography fontSize={12}>Transcript & Tasks</Typography>
      </DrawerOpenButton>
      <MeetingData
        sharedMeeting={sharedMeeting}
        event={event}
        eventLoading={eventLoading}
        usersList={usersList}
        meeting={meeting}
        onTimeClicked={onTimeClicked}
        meetingAnalysis={meetingAnalysis}
      />
      <MeetingDrawer
        open={open}
        setOpen={setOpen}
        reactPlayerRef={reactPlayerRef}
        transcriptContainerRef={transcriptContainerRef}
        setVideoParts={setVideoParts}
        onVideoProgress={onVideoProgress}
        onVideoReady={onVideoReady}
        meetingRecordingUrl={meetingRecordingUrl}
        meetingTranscriptUrl={meetingTranscriptUrl}
        sharedMeeting={sharedMeeting}
        meeting={meeting}
        onTimeClicked={onTimeClicked}
        markedId={markedId}
        setIsScrolling={setIsScrolling}
      />
    </Content>
  );
};

export default Meeting;
